<script setup>
  import { ref, onMounted, onUnmounted } from "vue";
  import { koKR, dateKoKR } from "naive-ui";
  import { createNaiveThemeOverrides } from "../naive.config";
  import { ensureAuth } from "@/lib/api";

  const themeOverrides = ref({});
  const authTimers = [];
  const AUTH_RECHECK_DELAYS = [0, 100, 300, 700, 1500, 3000];
  let sessionExpiredHandled = false;

  // 로그인 필요 없는 공개 경로
  const PUBLIC_PATHS = ["/oauth/callback", "/oauth/loginFail", "/logout-success"];

  function isPublicPath() {
    return PUBLIC_PATHS.some(p => window.location.pathname.startsWith(p));
  }

  async function checkAuth() {
    if (isPublicPath()) return;
    const hadToken = !!localStorage.getItem('accessToken');
    await ensureAuth();
    // localStorage가 늦게 보이기 시작한 경우에도 헤더가 현재 로그인 정보를 다시 읽게 한다.
    window.dispatchEvent(new CustomEvent('auth:refreshed'));
    // ensureAuth 후 토큰이 사라졌다면 → RT 만료로 로컬 세션 종료
    // 홈으로만 이동 (SAML SLO는 하지 않음 — SSO 세션은 유지)
    if (hadToken && !localStorage.getItem('accessToken') && !sessionExpiredHandled) {
      sessionExpiredHandled = true;
      alert('세션이 만료되었습니다. 다시 로그인해주세요.');
      window.location.href = '/';
    }
  }

  function onVisibilityChange() {
    if (document.visibilityState === "visible") {
      checkAuth();
    }
  }

  function onStorage(event) {
    if (event.key === "accessToken" || event.key === "user") {
      checkAuth();
    }
  }

  function onPageShow() {
    checkAuth();
  }

  function onFocus() {
    checkAuth();
  }

  function scheduleBootstrapChecks() {
    AUTH_RECHECK_DELAYS.forEach(delay => {
      const timer = window.setTimeout(
        checkAuth,
        delay,
      );
      authTimers.push(timer);
    });
  }

  onMounted(() => {
    themeOverrides.value = createNaiveThemeOverrides();

    // 리스너를 먼저 연결해 앱 시작과 WebKit 저장소 복원 사이의 이벤트 공백을 없앤다.
    document.addEventListener("visibilitychange", onVisibilityChange);
    window.addEventListener("storage", onStorage);
    window.addEventListener("pageshow", onPageShow);
    window.addEventListener("focus", onFocus);

    // iOS WebKit이 새 탭의 localStorage를 늦게 노출하더라도 제한 시간 동안 재확인한다.
    scheduleBootstrapChecks();
  });

  onUnmounted(() => {
    document.removeEventListener("visibilitychange", onVisibilityChange);
    window.removeEventListener("storage", onStorage);
    window.removeEventListener("pageshow", onPageShow);
    window.removeEventListener("focus", onFocus);
    authTimers.forEach(timer => window.clearTimeout(timer));
  });
</script>

<template>
  <n-config-provider
    :theme-overrides="themeOverrides"
    :locale="koKR"
    :date-locale="dateKoKR"
  >
    <n-dialog-provider>
      <router-view />
    </n-dialog-provider>
  </n-config-provider>
</template>
